/**
 * A lab's plan, and what state it is in. The same rules run on the server (to report it) and in
 * the lab's app (to remind the lab, also when it is offline), so they live here.
 */
export const PLAN_KINDS = ['subscription', 'validity', 'per_report'] as const;
export type PlanKind = (typeof PLAN_KINDS)[number];

export const PLAN_KIND_LABELS: Record<PlanKind, string> = {
  subscription: 'Monthly or yearly',
  validity: 'Fixed validity',
  per_report: 'Pay per report',
};

/** The part of a lab's plan the lab's app needs. Money is in paise. */
export interface LabPlan {
  name: string;
  kind: PlanKind;
  startedAt: string | null;
  /** When the plan runs out. Null: it does not. */
  expiresAt: string | null;
  period: 'monthly' | 'yearly' | null;
  wallet: { balancePaise: number; creditLimitPaise: number; reportPricePaise: number };
}

/** A lab is reminded this many days before its plan ends. */
export const ENDING_SOON_DAYS = 7;
/** A pay-per-report lab is told its wallet is low when it covers this many reports or fewer. */
export const LOW_WALLET_REPORTS = 10;

export type PlanState =
  | 'none' // no plan assigned
  | 'active' // a time plan with time left
  | 'ending' // a time plan ending within ENDING_SOON_DAYS
  | 'expired' // a time plan that has ended
  | 'ok' // wallet is healthy
  | 'low' // wallet covers only a few more reports
  | 'credit' // wallet is empty and the lab is using its credit limit
  | 'empty'; // wallet and credit are used up: no new reports

export interface PlanStatus {
  state: PlanState;
  kind: PlanKind | null;
  name: string;
  /** Whole days left on a time plan (0 on the last day, negative once ended). */
  daysLeft: number | null;
  expiresAt: string | null;
  /** Reports the wallet and credit still allow. Null when not a wallet plan or the price is zero. */
  reportsLeft: number | null;
  /** Only a spent-out wallet stops new reports. An ended time plan never does: it is a reminder. */
  canCreateReports: boolean;
  /** Worth showing the lab a notice about. */
  needsAttention: boolean;
}

const DAY = 86_400_000;

export function evaluatePlan(plan: LabPlan | null, now: Date = new Date()): PlanStatus {
  if (!plan) {
    return {
      state: 'none',
      kind: null,
      name: '',
      daysLeft: null,
      expiresAt: null,
      reportsLeft: null,
      canCreateReports: true,
      needsAttention: false,
    };
  }
  const base = { kind: plan.kind, name: plan.name, expiresAt: plan.expiresAt };

  if (plan.kind === 'per_report') {
    const { balancePaise: balance, creditLimitPaise: limit, reportPricePaise: price } = plan.wallet;
    if (price <= 0) {
      return {
        ...base,
        state: 'ok',
        daysLeft: null,
        reportsLeft: null,
        canCreateReports: true,
        needsAttention: false,
      };
    }
    const reportsLeft = Math.max(0, Math.floor((balance + limit) / price));
    const canCreateReports = reportsLeft >= 1;
    const funded = balance > 0 ? Math.floor(balance / price) : 0;
    const state: PlanState = !canCreateReports
      ? 'empty'
      : balance <= 0
        ? 'credit'
        : funded <= LOW_WALLET_REPORTS
          ? 'low'
          : 'ok';
    return {
      ...base,
      state,
      daysLeft: null,
      reportsLeft,
      canCreateReports,
      needsAttention: state !== 'ok',
    };
  }

  // subscription and validity plans: a date.
  if (!plan.expiresAt) {
    return {
      ...base,
      state: 'active',
      daysLeft: null,
      reportsLeft: null,
      canCreateReports: true,
      needsAttention: false,
    };
  }
  const ms = new Date(plan.expiresAt).getTime() - now.getTime();
  const daysLeft = ms >= 0 ? Math.ceil(ms / DAY) : Math.floor(ms / DAY);
  const state: PlanState = ms <= 0 ? 'expired' : daysLeft <= ENDING_SOON_DAYS ? 'ending' : 'active';
  return {
    ...base,
    state,
    daysLeft,
    reportsLeft: null,
    canCreateReports: true,
    needsAttention: state !== 'active',
  };
}

/** Add calendar months to a date (31 Jan + 1 month = end of February). */
export function addMonths(from: Date, months: number): Date {
  const d = new Date(from.getTime());
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d;
}

/** When a plan that starts now runs out. Null for a plan that does not run out. */
export function planEnd(
  plan: { kind: PlanKind; validityDays: number | null },
  period: 'monthly' | 'yearly' | null,
  start: Date,
): Date | null {
  if (plan.kind === 'per_report') return null;
  if (plan.kind === 'validity') {
    return plan.validityDays ? new Date(start.getTime() + plan.validityDays * DAY) : null;
  }
  return addMonths(start, period === 'yearly' ? 12 : 1);
}
